/**
 * The brand controls for a form's design template — colours, typography and the
 * control/layout shape.
 *
 * Extracted so there is ONE definition used in both places a theme can be
 * edited: Settings -> Form Design, where presets are managed, and the form
 * builder, where an author shapes the form and should be able to see a colour
 * change land on the live preview beside them without leaving for another
 * screen.
 *
 * Deliberately NOT included: the logo and the public-form background. Both
 * carry asset upload, both are brand setup done once rather than per form, and
 * dragging that machinery into the builder would buy little. The builder points
 * at Settings for them.
 *
 * Controlled: this holds no state. It takes the whole theme and hands back a
 * whole theme, so a caller can diff, undo, or decide whether a change is saved
 * to a shared preset or forked into a form's own copy — which is a question
 * only the caller can answer.
 */

import React from 'react';

import { ColorPicker } from '@/components/ui/ColorPicker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { FormTheme } from '@/components/forms/formTheme';

interface Props {
  theme: FormTheme;
  onChange: (next: FormTheme) => void;
  /** Hide the section/spacing/width controls where layout is edited elsewhere. */
  showLayout?: boolean;
}

const SELECT_CLASS =
  'w-full h-9 px-2 border rounded-md text-sm dark:bg-gray-800 dark:border-gray-600';

export const FormThemeControls: React.FC<Props> = ({ theme, onChange, showLayout = true }) => {
  const { t } = useAppTranslation();

  const setColor = (key: string, value: string) =>
    onChange({ ...theme, colors: { ...theme.colors, [key]: value } });
  const setControl = (key: string, value: string) =>
    onChange({ ...theme, controls: { ...theme.controls, [key]: value } });
  const setLayout = (key: string, value: string) =>
    onChange({ ...theme, layout: { ...theme.layout, [key]: value } });
  const setTypography = (key: string, value: string) =>
    onChange({ ...theme, typography: { ...theme.typography, [key]: value } });

  return (
    <div className="space-y-4">
      <section>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {t('forms.themeColors', 'Colors')}
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ColorPicker label={t('forms.themePrimary', 'Primary')} value={theme.colors.primary} onChange={(c) => setColor('primary', c)} />
          <ColorPicker label={t('forms.themeAccent', 'Accent')} value={theme.colors.accent} onChange={(c) => setColor('accent', c)} />
          <ColorPicker label={t('forms.themeText', 'Text')} value={theme.colors.text} onChange={(c) => setColor('text', c)} />
          <ColorPicker label={t('forms.themeMuted', 'Muted text')} value={theme.colors.muted} onChange={(c) => setColor('muted', c)} />
          <ColorPicker label={t('forms.themeBackground', 'Background')} value={theme.colors.background} onChange={(c) => setColor('background', c)} />
          <ColorPicker label={t('forms.themeSurface', 'Surface')} value={theme.colors.surface} onChange={(c) => setColor('surface', c)} />
          <ColorPicker label={t('forms.themeBorder', 'Border')} value={theme.colors.border} onChange={(c) => setColor('border', c)} />
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {t('forms.themeTypographyControls', 'Typography & controls')}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="sm:col-span-2 lg:col-span-1">
            <Label>{t('forms.themeFontFamily', 'Font family')}</Label>
            <Input
              value={theme.typography.font_family}
              onChange={(e) => setTypography('font_family', e.target.value)}
              placeholder="Inter, system-ui, sans-serif"
            />
          </div>
          <div>
            <Label>{t('forms.themeRadius', 'Corner radius')}</Label>
            <Input
              value={theme.controls.input_radius}
              onChange={(e) => setControl('input_radius', e.target.value)}
              placeholder="8px"
            />
          </div>
          <div>
            <Label>{t('forms.themeButtonStyle', 'Button style')}</Label>
            <select
              className={SELECT_CLASS}
              value={theme.controls.button_style}
              onChange={(e) => setControl('button_style', e.target.value)}
            >
              <option value="solid">{t('forms.themeSolid', 'Solid')}</option>
              <option value="outline">{t('forms.themeOutline', 'Outline')}</option>
              <option value="pill">{t('forms.themePill', 'Pill')}</option>
            </select>
          </div>

          {showLayout && (
            <>
              <div>
                <Label>{t('forms.themeSectionStyle', 'Section style')}</Label>
                <select
                  className={SELECT_CLASS}
                  value={theme.layout.section_style}
                  onChange={(e) => setLayout('section_style', e.target.value)}
                >
                  <option value="card">{t('forms.themeCard', 'Card')}</option>
                  <option value="flat">{t('forms.themeFlat', 'Flat')}</option>
                  <option value="divided">{t('forms.themeDivided', 'Divided')}</option>
                </select>
              </div>
              <div>
                <Label>{t('forms.themeSpacing', 'Spacing')}</Label>
                <select
                  className={SELECT_CLASS}
                  value={theme.layout.spacing}
                  onChange={(e) => setLayout('spacing', e.target.value)}
                >
                  <option value="comfortable">{t('forms.themeComfortable', 'Comfortable')}</option>
                  <option value="compact">{t('forms.themeCompact', 'Compact')}</option>
                </select>
              </div>
              <div>
                <Label>{t('forms.themeMaxWidth', 'Max width')}</Label>
                <select
                  className={SELECT_CLASS}
                  value={theme.layout.max_width}
                  onChange={(e) => setLayout('max_width', e.target.value)}
                >
                  <option value="640px">{t('forms.themeNarrow', 'Narrow (640px)')}</option>
                  <option value="768px">{t('forms.themeMedium', 'Medium (768px)')}</option>
                  <option value="900px">{t('forms.themeWide', 'Wide (900px)')}</option>
                  <option value="1100px">{t('forms.themeExtraWide', 'Extra wide (1100px)')}</option>
                  <option value="100%">{t('forms.themeFullWidth', 'Full width')}</option>
                </select>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default FormThemeControls;
